import { useEffect } from "react";
import { useRegisterSW } from "virtual:pwa-register/react";
import { toast } from "sonner";

/**
 * Listens for new service worker versions and prompts the user to reload.
 *
 * Why this exists: when we deploy a new version of the app or its edge
 * functions, users with the tab already open are running stale JS. Without
 * this hook they had to hard-refresh to pick up changes (the "Nera not
 * working until I do a hard refresh" issue). With autoUpdate + this prompt,
 * a fresh SW is fetched in the background and the user gets a one-click
 * reload toast as soon as it's ready.
 *
 * Hourly polling means a TPA who leaves the tab open all day still finds
 * out about a deploy without ever closing the tab.
 */
const UPDATE_POLL_INTERVAL_MS = 60 * 60 * 1000; // 1h

export function PWAUpdatePrompt() {
  const {
    needRefresh: [needRefresh, setNeedRefresh],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(_swUrl, registration) {
      if (!registration) return;
      // Periodically ask the SW to check for updates so long-lived tabs
      // pick up new deploys without requiring a manual refresh.
      setInterval(() => {
        registration.update().catch(() => {
          // Network errors are fine to swallow — we'll try again next tick.
        });
      }, UPDATE_POLL_INTERVAL_MS);
    },
    onRegisterError(error) {
      console.warn("Service worker registration failed:", error);
    },
  });

  useEffect(() => {
    if (!needRefresh) return;
    const id = toast("A new version is available", {
      description: "Reload to get the latest fixes and content.",
      duration: Infinity,
      action: {
        label: "Reload",
        onClick: () => {
          updateServiceWorker(true);
        },
      },
      onDismiss: () => setNeedRefresh(false),
    });
    return () => {
      toast.dismiss(id);
    };
  }, [needRefresh, setNeedRefresh, updateServiceWorker]);

  return null;
}
